import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Phone, ExternalLink, CheckCircle2, XCircle, AlertTriangle, Sparkles, Globe, Search, Layout, Shield, Lock, ChevronRight, Zap, Swords, Trophy, TrendingDown, Minus } from "lucide-react";
import QuizSection from "@/components/QuizSection";
import ScoreHistory from "@/components/ScoreHistory";
import WeeklyReportOptIn from "@/components/WeeklyReportOptIn";
import LeaderboardOptIn from "@/components/LeaderboardOptIn";

const PAYLINK = "https://a42f7c89-b7c9-4855-94e1-3be02f9ff2b2.paylinks.godaddy.com/055e91d5-0a77-470f-b034-cdf";

const statusIcon = {
  good: <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />,
  warning: <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />,
  bad: <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />,
};

const categoryIcons = { ai: Sparkles, seo: Search, technical: Zap, content: Layout, consultation: Phone };
const priorityStyles = { critical: "border-red-500/40 bg-red-500/5", high: "border-orange-500/40 bg-orange-500/5", medium: "border-zinc-700 bg-zinc-800/20" };

function ScoreRing({ score, label, color, size = "lg" }) {
  const radius = size === "lg" ? 70 : 40;
  const stroke = size === "lg" ? 8 : 5;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const dim = (radius + stroke) * 2;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: dim, height: dim }}>
        <svg width={dim} height={dim} className="transform -rotate-90">
          <circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke="#27272a" strokeWidth={stroke} />
          <motion.circle cx={radius + stroke} cy={radius + stroke} r={radius} fill="none" stroke={color} strokeWidth={stroke}
            strokeLinecap="round" strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }} animate={{ strokeDashoffset: offset }}
            transition={{ duration: 1.5, delay: 0.3, ease: "easeOut" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className={`font-black text-white ${size === "lg" ? "text-4xl" : "text-xl"}`}
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{score}</motion.span>
        </div>
      </div>
      <span className={`mt-2 font-semibold ${size === "lg" ? "text-sm" : "text-xs"} text-zinc-400`}>{label}</span>
    </div>
  );
}

function getScoreColor(score) {
  if (score >= 75) return "#22c55e";
  if (score >= 50) return "#3b82f6";
  if (score >= 25) return "#f59e0b";
  return "#ef4444";
}

function ComparisonBar({ label, yours, theirs, status }) {
  const maxVal = Math.max(yours, theirs, 1);
  return (
    <div className="py-3 border-b border-zinc-800/30 last:border-0">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm text-zinc-400 w-28 flex-shrink-0">{label}</span>
        <div className="flex-1 flex items-center gap-3">
          <div className="flex-1 flex justify-end">
            <motion.div initial={{ width: 0 }} animate={{ width: `${(yours / maxVal) * 100}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={`h-7 rounded-l-md flex items-center justify-end px-2 min-w-[40px] ${status === "winning" ? "bg-green-500/20 border border-green-500/30" : status === "losing" ? "bg-red-500/10 border border-red-500/20" : "bg-zinc-800 border border-zinc-700"}`}>
              <span className={`text-xs font-bold ${status === "winning" ? "text-green-400" : status === "losing" ? "text-red-400" : "text-zinc-400"}`}>{yours}</span>
            </motion.div>
          </div>
          <div className="w-px h-7 bg-zinc-700 flex-shrink-0" />
          <div className="flex-1">
            <motion.div initial={{ width: 0 }} animate={{ width: `${(theirs / maxVal) * 100}%` }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.1 }}
              className={`h-7 rounded-r-md flex items-center px-2 min-w-[40px] ${status === "losing" ? "bg-orange-500/15 border border-orange-500/25" : "bg-zinc-800/50 border border-zinc-800"}`}>
              <span className={`text-xs font-bold ${status === "losing" ? "text-orange-400" : "text-zinc-500"}`}>{theirs}</span>
            </motion.div>
          </div>
        </div>
        <div className="w-6 flex-shrink-0 ml-2">
          {status === "winning" && <Trophy className="w-4 h-4 text-green-500" />}
          {status === "losing" && <TrendingDown className="w-4 h-4 text-red-500" />}
          {status === "tied" && <Minus className="w-4 h-4 text-zinc-600" />}
        </div>
      </div>
    </div>
  );
}

export default function ResultsPage({ result, history, onQuizSubmit, onRescan, rescanLoading }) {
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizDone, setQuizDone] = useState(false);

  const { overall_score, seo_score, ai_visibility, seo_details, recommendations, lead_info, id, competitor } = result;
  const aiScore = ai_visibility?.overall_ai_score || 0;
  const chatgptScore = ai_visibility?.chatgpt_score || 0;
  const perplexityScore = ai_visibility?.perplexity_score || 0;
  const geminiScore = ai_visibility?.gemini_score || 0;
  const hasCompetitor = !!competitor;

  const handleQuizDone = (answers) => {
    onQuizSubmit(id, answers);
    setQuizDone(true);
    setShowQuiz(false);
  };

  const yourDomain = lead_info?.url?.replace(/https?:\/\//, '').replace(/\/$/, '').split('/')[0] || "Your Site";
  const compDomain = competitor?.url?.replace(/https?:\/\//, '').replace(/\/$/, '').split('/')[0] || "Competitor";

  return (
    <div className="min-h-screen relative" data-testid="results-page">
      <div className="absolute top-[-100px] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-[200px] pointer-events-none"
        style={{ background: `${getScoreColor(overall_score)}08` }} />

      {/* Header */}
      <header className="relative z-10 flex items-center justify-between max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <a href="https://geokath.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
          <div className="w-10 h-10 rounded-lg bg-orange-500 flex items-center justify-center font-black text-white text-lg" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>G</div>
          <div>
            <span className="text-white font-bold text-lg tracking-tight block leading-none" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Geokath Marketing</span>
            <span className="text-[10px] text-zinc-500 tracking-wider">AI Search Audit</span>
          </div>
        </a>
        <div className="hidden sm:flex items-center gap-4">
          <span className="text-xs text-zinc-500">Audit for: <span className="text-zinc-300">{yourDomain}</span></span>
          <a href="/leaderboard" className="text-xs text-zinc-500 hover:text-orange-500 transition-colors">Leaderboard</a>
        </div>
      </header>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 py-8 md:py-12">

        {/* Overall Score */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="text-center mb-16">
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500 mb-6 block">Your Audit Results</span>
          <div className="flex flex-col items-center mb-6">
            <ScoreRing score={overall_score} label="Overall Score" color={getScoreColor(overall_score)} size="lg" />
          </div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
            className="text-zinc-400 max-w-lg mx-auto text-base" data-testid="results-summary">
            {overall_score >= 75 ? "Strong online presence! But AI search is evolving fast — let's make sure you stay ahead."
              : overall_score >= 50 ? "Decent foundation, but critical gaps in AI visibility are costing you leads."
              : "Your business is largely invisible online. Competitors are capturing your potential customers."}
          </motion.p>
        </motion.div>

        {/* Score Breakdown */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6 mb-16" data-testid="score-breakdown">
          {[
            { score: seo_score, label: "SEO Score" },
            { score: aiScore, label: "AI Visibility" },
            { score: chatgptScore, label: "ChatGPT" },
            { score: perplexityScore, label: "Perplexity" },
            { score: geminiScore, label: "Gemini" },
          ].map((item, i) => (
            <div key={i} className={`flex flex-col items-center border border-zinc-800 rounded-xl p-5 bg-[#1a1a1c]/40 ${i === 4 ? "col-span-2 md:col-span-1" : ""}`}>
              <ScoreRing score={item.score} label={item.label} color={getScoreColor(item.score)} size="sm" />
            </div>
          ))}
        </motion.div>

        {/* COMPETITOR COMPARISON */}
        {hasCompetitor && competitor.comparison && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }}
            className="mb-12" data-testid="competitor-comparison">
            <div className="border-2 border-orange-500/20 rounded-2xl overflow-hidden">
              {/* Comparison header */}
              <div className="bg-gradient-to-r from-orange-500/10 to-transparent px-8 py-6 border-b border-orange-500/20">
                <div className="flex items-center gap-3 mb-3">
                  <Swords className="w-6 h-6 text-orange-500" />
                  <h3 className="text-2xl font-black tracking-tighter" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                    Head-to-Head Comparison
                  </h3>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-green-500" />
                    <span className="text-zinc-300 font-semibold">{competitor.wins} wins</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500" />
                    <span className="text-zinc-300 font-semibold">{competitor.losses} losses</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-zinc-600" />
                    <span className="text-zinc-400">{competitor.ties} tied</span>
                  </div>
                </div>
              </div>

              {/* Labels */}
              <div className="px-8 py-3 border-b border-zinc-800 flex items-center justify-between bg-[#1a1a1c]/30">
                <span className="text-xs uppercase tracking-wider font-bold text-zinc-500 w-28">Metric</span>
                <div className="flex-1 flex items-center">
                  <span className="flex-1 text-right text-xs uppercase tracking-wider font-bold text-green-500 pr-3">{yourDomain}</span>
                  <div className="w-px flex-shrink-0" />
                  <span className="flex-1 text-xs uppercase tracking-wider font-bold text-orange-500/70 pl-3">{compDomain}</span>
                </div>
                <div className="w-6" />
              </div>

              {/* Bars */}
              <div className="px-8 py-2">
                {competitor.comparison.map((item, i) => (
                  <ComparisonBar key={i} {...item} />
                ))}
              </div>

              {/* Verdict */}
              <div className="px-8 py-5 border-t border-zinc-800 bg-[#1a1a1c]/40">
                {competitor.wins > competitor.losses ? (
                  <div className="flex items-center gap-3">
                    <Trophy className="w-5 h-5 text-green-500" />
                    <p className="text-sm text-zinc-300">
                      <span className="font-bold text-green-400">You're ahead!</span> But there's still room to widen the gap and dominate your market.
                    </p>
                  </div>
                ) : competitor.wins < competitor.losses ? (
                  <div className="flex items-center gap-3">
                    <TrendingDown className="w-5 h-5 text-red-500" />
                    <p className="text-sm text-zinc-300">
                      <span className="font-bold text-red-400">Your competitor is outperforming you</span> in key areas. Action needed to close the gap and reclaim lost customers.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Swords className="w-5 h-5 text-orange-500" />
                    <p className="text-sm text-zinc-300">
                      <span className="font-bold text-orange-400">It's a close race!</span> Strategic moves now could give you the edge over your competitor.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Score History */}
        <ScoreHistory history={history} onRescan={onRescan} rescanLoading={rescanLoading} />

        {/* Weekly Report Opt-in */}
        <WeeklyReportOptIn url={lead_info?.url} email={lead_info?.email} name={lead_info?.name} />

        {/* Leaderboard Opt-in */}
        <LeaderboardOptIn leadId={id} overallScore={overall_score} />

        {/* AI Analysis */}
        {ai_visibility?.ai_summary && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.5 }}
            className="border border-zinc-800 rounded-xl p-8 bg-[#1a1a1c]/40 mb-10" data-testid="ai-analysis">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-orange-500" />
              <h3 className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>AI Search Analysis</h3>
            </div>
            <p className="text-zinc-400 text-sm leading-relaxed mb-5">{ai_visibility.ai_summary}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ai_visibility.strengths?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-green-500 font-bold mb-2">Strengths</h4>
                  <ul className="space-y-1.5">
                    {ai_visibility.strengths.map((s, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {ai_visibility.weaknesses?.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase tracking-wider text-red-500 font-bold mb-2">Weaknesses</h4>
                  <ul className="space-y-1.5">
                    {ai_visibility.weaknesses.map((w, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                        <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" /> {w}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* SEO Details */}
        {seo_details?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.6 }}
            className="border border-zinc-800 rounded-xl overflow-hidden mb-10" data-testid="seo-details">
            <div className="px-8 py-5 border-b border-zinc-800 bg-[#1a1a1c]/40 flex items-center gap-3">
              <Search className="w-5 h-5 text-orange-500" />
              <h3 className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>SEO Audit Details</h3>
            </div>
            <div className="divide-y divide-zinc-800/50">
              {seo_details.map((d, i) => (
                <div key={i} className="flex items-center gap-3 px-8 py-3.5 hover:bg-[#1a1a1c]/30 transition-colors">
                  {statusIcon[d.status]}
                  <span className="text-sm font-semibold text-zinc-300 w-40 flex-shrink-0">{d.item}</span>
                  <span className="text-sm text-zinc-500">{d.note}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Recommendations */}
        {recommendations?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.7 }}
            className="mb-12" data-testid="recommendations-section">
            <h3 className="text-2xl font-black tracking-tighter mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Recommended Actions</h3>
            <p className="text-zinc-500 text-sm mb-6">Prioritized fixes to improve your visibility:</p>
            <div className="space-y-3">
              {recommendations.map((rec, i) => {
                const Icon = categoryIcons[rec.category] || Globe;
                return (
                  <motion.div key={i} initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.8 + i * 0.08 }}
                    className={`p-5 rounded-xl border backdrop-blur-xl ${priorityStyles[rec.priority] || priorityStyles.medium}`}
                    data-testid={`recommendation-${i}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-9 h-9 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-orange-500" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-white text-sm">{rec.title}</h4>
                          {rec.priority === "critical" && <span className="text-[10px] uppercase tracking-wider font-bold text-red-500 bg-red-500/10 px-2 py-0.5 rounded">Critical</span>}
                          {rec.priority === "high" && <span className="text-[10px] uppercase tracking-wider font-bold text-orange-500 bg-orange-500/10 px-2 py-0.5 rounded">Priority</span>}
                        </div>
                        <p className="text-zinc-400 text-sm">{rec.description}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Paid Roadmap Upsell */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 1 }}
          className="border-2 border-orange-500/30 rounded-2xl p-8 md:p-10 mb-10 relative overflow-hidden" data-testid="roadmap-upsell">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 to-transparent pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <Lock className="w-5 h-5 text-orange-500" />
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500">Unlock Full Roadmap</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-black tracking-tighter mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Get Your Complete Fix-It Roadmap
            </h3>
            <p className="text-zinc-400 mb-6 max-w-xl">
              Get a step-by-step action plan to fix every issue — with exact instructions, priority order, and expected impact.
            </p>
            <ul className="space-y-2 mb-8">
              {["Detailed technical SEO fix instructions", "AI search optimization checklist", "Content strategy recommendations", "Competitor gap analysis", "Monthly monitoring checklist"].map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-zinc-300">
                  <CheckCircle2 className="w-4 h-4 text-orange-500 flex-shrink-0" /> {item}
                </li>
              ))}
            </ul>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <a href={PAYLINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold py-3.5 px-8 rounded-md transition-all duration-300 hover:bg-orange-600 shadow-[0_0_25px_rgba(249,115,22,0.35)] hover:shadow-[0_0_40px_rgba(249,115,22,0.5)]"
                data-testid="buy-roadmap-button">
                Get Full Roadmap — $49 <ChevronRight className="w-4 h-4" />
              </a>
              <span className="text-zinc-600 text-xs">One-time payment. Delivered via email.</span>
            </div>
          </div>
        </motion.div>

        {/* Quiz */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 1.1 }}
          className="mb-12" data-testid="quiz-section-wrapper">
          {!showQuiz && !quizDone && (
            <div className="border border-zinc-800 rounded-xl p-8 bg-[#1a1a1c]/40 text-center">
              <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Want More Personalized Recommendations?
              </h3>
              <p className="text-zinc-500 text-sm mb-6">Answer 6 quick questions to get recommendations tailored to your specific business situation.</p>
              <button onClick={() => setShowQuiz(true)}
                className="inline-flex items-center gap-2 border border-orange-500/50 text-orange-500 font-semibold py-3 px-6 rounded-md hover:bg-orange-500/10 transition-colors"
                data-testid="start-quiz-button">
                Take Quick Assessment <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}
          <AnimatePresence>
            {showQuiz && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                <QuizSection onComplete={handleQuizDone} />
              </motion.div>
            )}
          </AnimatePresence>
          {quizDone && (
            <div className="border border-green-500/30 rounded-xl p-6 bg-green-500/5 text-center">
              <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-2" />
              <p className="text-sm text-zinc-300">Thanks! Your responses have been recorded.</p>
            </div>
          )}
        </motion.div>

        {/* Main CTA */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 1.3 }}
          className="border border-orange-500/20 rounded-2xl p-8 md:p-12 backdrop-blur-xl bg-orange-500/5 mb-12"
          data-testid="cta-section">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <img src="https://customer-assets.emergentagent.com/job_traffic-magnet-8/artifacts/vubacfhk_profile-pic.png"
              alt="Geoff Clark" className="w-24 h-24 md:w-28 md:h-28 rounded-full object-cover border-4 border-orange-500/30 flex-shrink-0" />
            <div className="text-center md:text-left flex-1">
              <h3 className="text-2xl md:text-3xl font-black tracking-tighter mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                Let's Fix This Together
              </h3>
              <p className="text-zinc-400 mb-6 max-w-lg">
                I'm Geoff Clark — I've been helping Sacramento businesses dominate online since 1996.
                Book a free 30-minute strategy call and I'll personally review your audit results and build you an action plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="https://geokath.com/contact" target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 text-white font-bold py-3.5 px-7 rounded-md transition-all duration-300 hover:bg-orange-600 shadow-[0_0_25px_rgba(249,115,22,0.4)] hover:shadow-[0_0_40px_rgba(249,115,22,0.6)]"
                  data-testid="book-consultation-button">
                  Book Free Strategy Call <ArrowRight className="w-4 h-4" />
                </a>
                <a href="tel:909-800-5616"
                  className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-white font-semibold py-3.5 px-7 rounded-md transition-all duration-300 hover:border-zinc-500 hover:bg-zinc-800/50"
                  data-testid="call-now-button">
                  <Phone className="w-4 h-4" /> 909-800-5616
                </a>
              </div>
              <p className="text-zinc-600 text-xs mt-4">Mon-Fri 9am-6pm Pacific | No pressure, no obligation</p>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <div className="pt-8 border-t border-zinc-800 flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
          <a href="https://geokath.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-6 h-6 rounded bg-orange-500 flex items-center justify-center font-black text-white text-xs" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>G</div>
            <span className="text-sm text-zinc-500">Geokath Marketing</span>
          </a>
          <div className="flex items-center gap-4 text-xs text-zinc-600">
            <a href="https://geokath.com/services" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">Services</a>
            <a href="/leaderboard" className="hover:text-zinc-400 transition-colors">Leaderboard</a>
            <a href="https://geokath.com/contact" target="_blank" rel="noopener noreferrer" className="hover:text-zinc-400 transition-colors">Contact</a>
            <span>Sacramento, CA | Since 1996</span>
          </div>
        </div>
      </div>
    </div>
  );
}
